import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { adminLogin, getToken } from "../lib/api";
import {
  ArrowRight,
  Lock,
  Mail,
  KeyRound,
  Loader2,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";

// First-line passcode gate (defense in depth). Stored in sessionStorage so
// it clears when the browser tab is closed. Configurable via env at build
// time — falls back to the demo passcode below.
const ADMIN_PASSCODE = process.env.REACT_APP_ADMIN_PASSCODE || "investhub2026";
const PASSCODE_KEY = "ihub_admin_passcode_ok";

export function isPasscodeUnlocked() {
  try {
    return sessionStorage.getItem(PASSCODE_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearPasscode() {
  try {
    sessionStorage.removeItem(PASSCODE_KEY);
  } catch {
    /* ignore */
  }
}

export default function AdminLogin() {
  const navigate = useNavigate();
  // Always read the live sessionStorage value on every render so the
  // component can never get stuck on step 2 once the passcode flag is
  // cleared (e.g. by signing out from /admin).
  const passcodeOk = isPasscodeUnlocked();
  const [stage, setStage] = useState(passcodeOk ? "credentials" : "passcode");

  // If sessionStorage says "locked" but our internal stage thinks we're
  // on credentials, snap back to step 1 immediately.
  if (!passcodeOk && stage === "credentials") {
    // Calling setState during render is safe here: it short-circuits and
    // re-renders with the corrected stage.
    setStage("passcode");
  }

  // Passcode stage
  const [pass, setPass] = useState("");
  const [passError, setPassError] = useState("");

  // Credentials stage
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [credError, setCredError] = useState("");
  const [loading, setLoading] = useState(false);

  // Already fully signed in? skip everything.
  useEffect(() => {
    if (isPasscodeUnlocked() && getToken()) {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);

  const tryPasscode = (e) => {
    e.preventDefault();
    setPassError("");
    if (pass.trim() === ADMIN_PASSCODE) {
      try {
        sessionStorage.setItem(PASSCODE_KEY, "1");
      } catch {
        /* ignore */
      }
      setStage("credentials");
    } else {
      setPassError("Incorrect passcode.");
    }
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setCredError("");
    setLoading(true);
    try {
      await adminLogin(email.trim().toLowerCase(), password);
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setCredError(typeof detail === "string" ? detail : "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <section className="flex-1 bg-[#f7faf9] py-12 lg:py-20">
        <div className="max-w-md mx-auto px-5">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-5">
            <StepDot label="1" active={stage === "passcode"} done={stage !== "passcode"} title="Passcode" />
            <div className="w-8 h-px bg-[#0d1a2e]/15" />
            <StepDot label="2" active={stage === "credentials"} done={false} title="Sign-in" />
          </div>

          <div className="bg-white border border-[#0d1a2e]/10 rounded-3xl p-7 lg:p-10">
            {stage === "passcode" ? (
              <>
                <div className="w-14 h-14 rounded-2xl bg-[#0d1a2e] mx-auto flex items-center justify-center">
                  <ShieldAlert className="w-6 h-6 text-[#16d196]" strokeWidth={2.2} />
                </div>
                <h1 className="mt-5 text-[28px] font-extrabold text-[#0d1a2e] tracking-[-0.02em] text-center">
                  Admin <span className="text-[#16d196]">passcode</span>
                </h1>
                <p className="mt-2 text-[#0d1a2e]/60 text-[14px] text-center">
                  Step 1 of 2 · enter the shared team passcode to continue.
                </p>

                <form onSubmit={tryPasscode} className="mt-7 space-y-4">
                  <div>
                    <label className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">
                      Passcode
                    </label>
                    <div className="relative mt-2">
                      <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#0d1a2e]/45" />
                      <input
                        type="password"
                        required
                        value={pass}
                        onChange={(e) => setPass(e.target.value)}
                        placeholder="Enter passcode"
                        className="w-full h-12 bg-[#f7faf9] border-2 border-[#0d1a2e]/10 focus:border-[#16d196] outline-none rounded-xl pl-11 pr-4 text-[14.5px]"
                        autoFocus
                      />
                    </div>
                  </div>

                  {passError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-[13px] text-red-700 font-semibold">
                      {passError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full h-12 inline-flex items-center justify-center gap-1.5 bg-[#0d1a2e] hover:bg-black text-white rounded-full font-bold transition-colors"
                  >
                    Continue <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                  </button>
                </form>

                <div className="mt-7 pt-5 border-t border-[#0d1a2e]/8 flex items-start gap-2 text-[11.5px] text-[#0d1a2e]/55">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#16d196] shrink-0 mt-0.5" />
                  <span>
                    Authorised personnel only. Forgot your passcode?
                    Contact your administrator.
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-2xl bg-[#16d196] mx-auto flex items-center justify-center shadow-[0_8px_24px_rgba(22,209,150,0.35)]">
                  <Lock className="w-6 h-6 text-[#0d1a2e]" strokeWidth={2.2} />
                </div>
                <h1 className="mt-5 text-[28px] font-extrabold text-[#0d1a2e] tracking-[-0.02em] text-center">
                  Admin <span className="text-[#16d196]">sign-in</span>
                </h1>
                <p className="mt-2 text-[#0d1a2e]/60 text-[14px] text-center">
                  Step 2 of 2 · authorised team members only. 24h session.
                </p>

                <form onSubmit={submitCreds} className="mt-7 space-y-4">
                  <div>
                    <label className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">
                      Email
                    </label>
                    <div className="relative mt-2">
                      <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#0d1a2e]/45" />
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@investhubuk.co.uk"
                        className="w-full h-12 bg-[#f7faf9] border-2 border-[#0d1a2e]/10 focus:border-[#16d196] outline-none rounded-xl pl-11 pr-4 text-[14.5px]"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[12px] uppercase tracking-[0.14em] text-[#0d1a2e]/60 font-bold">
                      Password
                    </label>
                    <div className="relative mt-2">
                      <KeyRound className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#0d1a2e]/45" />
                      <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full h-12 bg-[#f7faf9] border-2 border-[#0d1a2e]/10 focus:border-[#16d196] outline-none rounded-xl pl-11 pr-4 text-[14.5px]"
                      />
                    </div>
                  </div>

                  {credError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-[13px] text-red-700 font-semibold">
                      {credError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 inline-flex items-center justify-center gap-1.5 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] rounded-full font-bold transition-colors disabled:opacity-60 shadow-[0_8px_24px_rgba(22,209,150,0.3)]"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Signing in…
                      </>
                    ) : (
                      <>
                        Sign in
                        <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      clearPasscode();
                      setStage("passcode");
                      setPass("");
                    }}
                    className="w-full text-center text-[12px] text-[#0d1a2e]/55 hover:text-[#0d1a2e] mt-1"
                  >
                    ← Re-enter passcode
                  </button>
                </form>

                <div className="mt-7 pt-5 border-t border-[#0d1a2e]/8 flex items-start gap-2 text-[11.5px] text-[#0d1a2e]/55">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#16d196] shrink-0 mt-0.5" />
                  <span>
                    Sessions expire after 24 hours. Failed sign-ins are
                    logged.
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-[13px] text-[#0d1a2e]/55 hover:text-[#16d196]">
              ← Back to homepage
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function StepDot({ label, active, done, title }) {
  const cls = done
    ? "bg-[#16d196] text-[#0d1a2e]"
    : active
    ? "bg-[#0d1a2e] text-white"
    : "bg-[#0d1a2e]/8 text-[#0d1a2e]/55";
  return (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold ${cls}`}>
        {done ? "✓" : label}
      </div>
      <span className={`text-[11.5px] font-bold uppercase tracking-[0.14em] ${active || done ? "text-[#0d1a2e]" : "text-[#0d1a2e]/45"}`}>
        {title}
      </span>
    </div>
  );
}
